import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { provideTranslocoScope } from "@jsverse/transloco";
import { guid } from "@methods/uniqueMethods";
import { isObservable, Observable } from "rxjs";

type GetDataMethod = Observable<any[]> | Promise<any[]> | any[];

@Component({
    selector: "cerrix-select",
    templateUrl: "./cerrix-select.component.html",
    styleUrls: ["./cerrix-select.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: CerrixSelectComponent,
            multi: true,
        },
        provideTranslocoScope({ scope: "shared/cerrix-select", alias: "cerrixSelect" }),
    ],
    standalone: false
})
export class CerrixSelectComponent implements OnInit, ControlValueAccessor {
    private readonly _destroyRef = inject(DestroyRef);

    valid = true;
    _value: number | number[];

    _onChangeFormValidation = Function.prototype;
    _onTouchedFormValidation = Function.prototype;

    @Input() id: string;
    @Input() prettyName: string;
    @Input() disabled = false;

    @Input() multiple = false;
    @Input() closeOnSelect = true;
    @Input() addTag = false;
    @Input() placeholder = "";
    @Input() clearable = true;
    @Input() searchable = true;

    private _getDataMethod: GetDataMethod;
    private _initialized = false;

    @Input()
    set getDataMethod(value: GetDataMethod) {
        const wasNullish = this._getDataMethod == null;
        this._getDataMethod = value;

        // The first load runs in ngOnInit once every input is set. After that, only
        // reload when a previously absent source arrives (e.g. a parent assigns the
        // request observable in an async ngOnInit). A method-call binding that always
        // returns a fresh non-null value must not re-trigger a load on every change.
        if (this._initialized && wasNullish && value != null) {
            this.reloadData();
        }
    }
    get getDataMethod(): GetDataMethod {
        return this._getDataMethod;
    }

    @Input() bindValue: string = "ID";
    @Input() bindLabel: string = "Name";
    @Input() bindColor: string = "Color";
    @Input() ngModelOptions = {};

    @Input() dataUiid: string = guid();

    @Output() valueChange = new EventEmitter<number | number[]>();

    @Input()
    set value(value: number | number[]) {
        if (this._value === value) {
            return;
        }

        this._value = value;
        this.valueChange.emit(value);
        this._onChangeFormValidation(value);
    }
    get value(): number | number[] {
        return this._value;
    }

    data: any[];
    showNoData: boolean;
    showRefreshButton = true;

    constructor() {}

    ngOnInit(): void {
        this._initialized = true;
        this.reloadData();
    }

    reloadData() {
        this.data = null;
        this.showNoData = false;

        // Nothing to load yet (an async parent may assign getDataMethod later). Leave
        // data null so the loading spinner shows; the setter reloads once it arrives.
        if (this._getDataMethod == null) {
            return;
        }

        const handler = (data: any[]) => {
            this.data = data;
            this.showNoData = this.prettyName && this.data.length === 0;

            let intersects;
            if (Array.isArray(this.value)) {
                intersects = !data.some((x) =>
                    (this.value as []).some((value) => x[this.bindValue] == value)
                );
            } else {
                intersects = !data.some((x) => x[this.bindValue] == this.value);
            }

            if (intersects) {
                if (this.multiple) {
                    this.value = [];
                } else {
                    this.value = null;
                }
            }
        };

        if (isObservable(this._getDataMethod)) {
            (<Observable<any>>this._getDataMethod)
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe(handler);
        } else if (this._getDataMethod instanceof Promise) {
            this._getDataMethod.then(handler);
        } else {
            handler(JSON.parse(JSON.stringify(this._getDataMethod)));
        }
    }

    // Form Validation
    writeValue(obj: any): void {
        this.value = obj;
    }
    registerOnChange(fn: any): void {
        this._onChangeFormValidation = fn;
    }
    registerOnTouched(fn: any): void {
        this._onTouchedFormValidation = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
